import { Heading } from '@assembly-js/design-system';
import { Grid, IconButton, MenuItem } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { TrashIcon } from 'src/components/icons';
import { VisibilityComparator, VisibilityRuleType } from 'src/constants';
import { useVisibilityRuleFields } from 'src/hooks/useVisibilityRuleFields';
import { BaseTextField } from 'src/legacy/components/TextField';
import {
  BlackHeadings,
  DarkFont,
  DividersAndCardBorders,
  GraySmall,
  HoverBackground,
  HoverBorder,
  NonHoverBorder,
  white,
} from 'src/theme/colors';
import { DropdownMenuShadow } from 'src/theme/shadows';

import { VisibilityRecipientSelector } from './VisibilityRecipientSelector';

export {
  type ComparatorField,
  getComparatorRules,
  type VisibilityRecipient,
} from 'src/utils/app_visibility';

const useStyles = makeStyles()((theme) => ({
  root: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      borderRadius: 0,
    },
  },
  select: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      borderRadius: '4px 0 0 4px',
    },
  },
  autoComplete: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      height: '36px',
      borderRadius: '0 4px 4px 0',
      paddingRight: theme.spacing(1.5),
      paddingLeft: theme.spacing(1),
    },
  },
  dropdownIcon: {
    height: '15px',
    width: '15px',
    color: GraySmall,
  },
  list: {
    padding: theme.spacing(0.75, 0),
  },
  listPaper: {
    border: `1px solid ${NonHoverBorder}`,
    boxShadow: DropdownMenuShadow,
    '& .MuiAutocomplete-groupUl': {
      '& .MuiAutocomplete-option': {
        paddingLeft: theme.spacing(2),
      },
    },
  },
  inputRoots: {
    '& .MuiAutocomplete-endAdornment': {
      marginRight: theme.spacing(0.25),
      backgroundColor: white,
    },
  },
  baseChip: {
    borderRadius: 100,
    color: BlackHeadings,
    backgroundColor: HoverBackground,
    border: `1px solid ${NonHoverBorder}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: DividersAndCardBorders,
    },
    '&.MuiChip-root': {
      height: 'auto',
      '& span': {
        padding: 0,
      },
    },
    '&.MuiAutocomplete-tag': {
      margin: 0,
    },
  },
  closeIcon: {
    display: 'none',
  },
  avatarContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1.5),
    gap: theme.spacing(0.75),
  },

  chipLabel: {
    flexShrink: 0,
    maxWidth: '110px',
  },
  deleteIcon: {
    border: `1px solid ${NonHoverBorder}`,
    borderRadius: theme.shape.borderRadius,
    height: theme.spacing(4.5),
    width: theme.spacing(4.5),
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    justifyContent: 'center',
  },
  trashIcon: {
    fontSize: '20px',
    color: HoverBorder,
  },
  or: {
    color: DarkFont,
    marginBottom: theme.spacing(0.75),
  },

  truncatedText: {
    display: 'inline-block',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  tagIcon: {
    display: 'flex',
  },
  icon: {
    position: 'relative',
    '&:nth-child(2)': {
      left: '-2px',
    },
    '&:nth-child(3)': {
      left: '-4px',
    },
  },
}));

type Props = {
  ruleIndex: number;
  onDeleteRule: () => void;
};

export const AppVisibilityRule: React.FC<Props> = ({
  ruleIndex,
  onDeleteRule,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const {
    handleChange,
    clients,
    clientCompanyFields,
    clientCustomFieldsDropdown,
    companyCustomFieldsDropdown,
    selectedFieldValues,
    handleFieldChange,
    comparatorDropdownRules,
    isCheckboxField,
    selectedComparatorValue,
    selectedRuleType,
    selectedValues,
    clientSelectedRecipients,
    handleClientRecipientChange,
    optionDropdownHelperText,
    isAppVisibilityFeatureEnabled,
    values,
  } = useVisibilityRuleFields(ruleIndex);

  return (
    <Grid container className={classes.root} spacing={3}>
      <Grid xs={values.visibilityConfig?.length > 1 ? 11 : 12} item container>
        <Grid item xs={4} className={classes.select}>
          <BaseTextField
            fullWidth
            select
            sizeVariant="medium"
            variant="outlined"
            value={selectedFieldValues.id}
            onChange={handleFieldChange}
          >
            {clientCompanyFields.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.label}
              </MenuItem>
            ))}

            {clientCustomFieldsDropdown.length > 0 && (
              <MenuItem disabled style={{ opacity: 1 }}>
                <Heading size="xs" className="text-gray-small">
                  {t(($) => $.page_apps.client_custom_fields)}
                </Heading>
              </MenuItem>
            )}

            {clientCustomFieldsDropdown.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.label}
              </MenuItem>
            ))}

            {companyCustomFieldsDropdown.length > 0 && (
              <MenuItem disabled style={{ opacity: 1 }}>
                <Heading size="xs" className="text-gray-small">
                  {t(($) => $.page_apps.company_custom_fields)}
                </Heading>
              </MenuItem>
            )}

            {companyCustomFieldsDropdown.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.label}
              </MenuItem>
            ))}
          </BaseTextField>
        </Grid>
        <Grid item xs={4}>
          <BaseTextField
            fullWidth
            select
            sizeVariant="medium"
            variant="outlined"
            name={`visibilityConfig.${ruleIndex}.comparator`}
            value={
              comparatorDropdownRules.find(
                (r) => r.id === selectedComparatorValue,
              )?.id || VisibilityComparator.IncludesAnyOf
            }
            onChange={handleChange}
          >
            {comparatorDropdownRules.map((comparatorRule) => (
              <MenuItem key={comparatorRule.id} value={comparatorRule.id}>
                {selectedValues?.length > 1
                  ? comparatorRule.pluralLabel
                  : comparatorRule.singularLabel}
              </MenuItem>
            ))}
          </BaseTextField>
        </Grid>
        <Grid
          item
          xs={4}
          className={
            selectedRuleType === VisibilityRuleType.Client &&
            isAppVisibilityFeatureEnabled
              ? undefined
              : classes.autoComplete
          }
        >
          {!isCheckboxField && (
            <VisibilityRecipientSelector
              ruleIndex={ruleIndex}
              recipients={clientSelectedRecipients.recipients}
              clients={clients}
              selectedFieldValues={selectedFieldValues}
              selectedRuleType={selectedRuleType}
              onRecipientSelected={handleClientRecipientChange}
              error={!!optionDropdownHelperText}
              helperText={
                typeof optionDropdownHelperText === 'string'
                  ? optionDropdownHelperText
                  : undefined
              }
            />
          )}
        </Grid>
      </Grid>
      {values.visibilityConfig?.length > 1 && (
        <Grid item xs={1}>
          <div className={classes.deleteIcon}>
            <IconButton onClick={onDeleteRule}>
              <TrashIcon className={classes.trashIcon} />
            </IconButton>
          </div>
        </Grid>
      )}
    </Grid>
  );
};
